import type { PageMeta, Permission } from '@mm/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Search, Trash2 } from 'lucide-react';
import { type ReactNode, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { z } from 'zod';
import { api, ApiError, type QueryParams } from '../../api/client';
import { Button } from '../../components/ui/Button';
import { Card, PageHeader } from '../../components/ui/display';
import { Input } from '../../components/ui/inputs';
import { type RemoteSource, SearchableSelect } from '../../components/ui/SearchableSelect';
import { ConfirmDialog, Modal } from '../../components/ui/Modal';
import { useToast } from '../../components/ui/Toast';
import {
  type FieldDef,
  type FormValues,
  type Option,
  ResourceForm,
} from '../../forms/ResourceForm';
import { useDebounce } from '../../hooks/useDebounce';
import { LOOKUPS_KEY, useLookups } from '../../hooks/useLookups';
import { type Column, DataTable } from '../../tables/DataTable';
import type { Lookups } from '../../types/models';
import { useAuth } from '../auth/AuthProvider';

export interface FilterDef {
  name: string;
  label: string;
  /** Small fixed list (searched locally) … */
  options?: Option[] | ((lookups: Lookups | undefined) => Option[]);
  /** … or a searchable server-side source. */
  remote?: RemoteSource;
}

export interface MasterConfig<T> {
  /** API collection path, e.g. 'advertising-accounts'. */
  resource: string;
  title: string;
  singular: string;
  description?: ReactNode;
  keyOf?: (row: T) => string;
  columns: Column<T>[];
  fields: FieldDef[];
  schema: z.ZodType;
  defaults: (lookups: Lookups | undefined) => FormValues;
  toForm: (row: T) => FormValues;
  writePermission: Permission;
  filters?: FilterDef[];
  defaultSort?: string;
  rowLink?: (row: T) => string;
  searchPlaceholder?: string;
  allowDelete?: boolean;
  pageSize?: number;
  /** Runs after a successful create / update (e.g. read details from Google Play). */
  afterSave?: (saved: T, previous: T | null) => Promise<void>;
}

export const resourceKey = (resource: string) => ['resource', resource] as const;

/** 'App' → 'app', but keep acronyms and brand names: 'GST account', 'AdMob account', 'MCC'. */
const lowerFirst = (s: string) => {
  const first = s.split(' ')[0] ?? '';
  return /[A-Z]/.test(first.slice(1)) ? s : s.charAt(0).toLowerCase() + s.slice(1);
};

type Editing<T> = { mode: 'create' } | { mode: 'edit'; row: T } | null;

/** Generic list + create/edit/delete page for a master-data resource. */
export function MasterPage<T extends { updatedAt?: string }>(props: {
  config: MasterConfig<T>;
  embedded?: boolean;
}) {
  // One instance per resource: the router reuses this component between list
  // pages, and the previous list's rows, filters and paging must not carry over.
  return <MasterList key={props.config.resource} {...props} />;
}

function MasterList<T extends { updatedAt?: string }>({
  config,
  embedded = false,
}: {
  config: MasterConfig<T>;
  embedded?: boolean;
}) {
  const { can } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: lookups } = useLookups();

  const [page, setPage] = useState(1);
  const [sort, setSort] = useState(config.defaultSort);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [editing, setEditing] = useState<Editing<T>>(null);
  const [deleting, setDeleting] = useState<T | null>(null);
  const debouncedSearch = useDebounce(search);

  const keyOf = config.keyOf ?? ((row: T) => (row as unknown as { id: string }).id);
  const canWrite = can(config.writePermission);

  const params: QueryParams = {
    page,
    pageSize: config.pageSize ?? 25,
    sort,
    search: debouncedSearch,
    ...filters,
  };
  const query = useQuery({
    queryKey: [...resourceKey(config.resource), 'list', params],
    queryFn: () => api.list<T>(`/${config.resource}`, params),
    placeholderData: keepPreviousData,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: resourceKey(config.resource) });
    void queryClient.invalidateQueries({ queryKey: LOOKUPS_KEY });
  };

  const save = async (values: FormValues) => {
    let saved: T;
    const previous = editing?.mode === 'edit' ? editing.row : null;
    if (previous) {
      saved = await api.put<T>(`/${config.resource}/${encodeURIComponent(keyOf(previous))}`, {
        ...values,
        updatedAt: previous.updatedAt,
      });
      toast.success(`${config.singular} updated`);
    } else {
      saved = await api.post<T>(`/${config.resource}`, values);
      toast.success(`${config.singular} created`);
    }
    setEditing(null);
    if (config.afterSave) {
      await config
        .afterSave(saved, previous)
        .catch((err) =>
          toast.error(
            err instanceof ApiError ? err.message : 'Saved, but the follow-up step failed',
          ),
        );
    }
    invalidate();
  };

  const remove = useMutation({
    mutationFn: (row: T) => api.delete(`/${config.resource}/${encodeURIComponent(keyOf(row))}`),
    onSuccess: () => {
      toast.success(`${config.singular} deleted`);
      setDeleting(null);
      invalidate();
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : 'Delete failed');
      setDeleting(null);
    },
  });

  const columns = useMemo<Column<T>[]>(() => {
    if (!canWrite) return config.columns;
    return [
      ...config.columns,
      {
        key: '__actions',
        header: <span className="sr-only">Actions</span>,
        align: 'right',
        render: (row) => (
          <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => setEditing({ mode: 'edit', row })}
              className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
              aria-label={`Edit ${lowerFirst(config.singular)}`}
            >
              <Pencil className="size-4" />
            </button>
            {config.allowDelete !== false && (
              <button
                type="button"
                onClick={() => setDeleting(row)}
                className="rounded-md p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600"
                aria-label={`Delete ${lowerFirst(config.singular)}`}
              >
                <Trash2 className="size-4" />
              </button>
            )}
          </div>
        ),
      },
    ];
  }, [canWrite, config]);

  const addButton = canWrite && (
    <Button icon={<Plus className="size-4" />} onClick={() => setEditing({ mode: 'create' })}>
      Add {lowerFirst(config.singular)}
    </Button>
  );

  const toolbar = (
    <div className="flex flex-col gap-3 border-b border-slate-100 p-4 sm:flex-row sm:items-center">
      <div className="relative w-full sm:max-w-xs">
        <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-slate-400" />
        <Input
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          placeholder={config.searchPlaceholder ?? 'Search…'}
          className="pl-9"
          aria-label="Search"
        />
      </div>
      {config.filters?.map((f) => {
        const options = typeof f.options === 'function' ? f.options(lookups) : f.options;
        return (
          <SearchableSelect
            key={f.name}
            ariaLabel={f.label}
            className="sm:w-56"
            value={filters[f.name] ?? ''}
            options={options}
            remote={f.remote}
            clearable
            placeholder={`All ${f.label.toLowerCase()}`}
            onChange={(v) => {
              setFilters((all) => ({ ...all, [f.name]: v }));
              setPage(1);
            }}
          />
        );
      })}
    </div>
  );

  const formId = `form-${config.resource}`;
  const editRow = editing?.mode === 'edit' ? editing.row : null;

  return (
    <>
      {!embedded && (
        <PageHeader title={config.title} description={config.description} actions={addButton} />
      )}
      <Card
        bodyClassName="p-0"
        title={embedded ? config.title : undefined}
        description={embedded ? config.description : undefined}
        actions={embedded ? addButton : undefined}
      >
        {toolbar}
        <DataTable
          tableId={config.resource}
          columns={columns}
          rows={query.data?.data}
          rowKey={keyOf}
          loading={query.isLoading}
          sort={sort}
          onSortChange={(s) => {
            setSort(s);
            setPage(1);
          }}
          onRowClick={config.rowLink ? (row) => navigate(config.rowLink!(row)) : undefined}
          meta={query.data?.meta as PageMeta | undefined}
          onPageChange={setPage}
        />
        {query.isError && (
          <p className="p-4 text-sm text-rose-600">
            {query.error instanceof ApiError ? query.error.message : 'Failed to load data'}
          </p>
        )}
      </Card>

      <Modal
        open={editing !== null}
        onClose={() => setEditing(null)}
        title={
          editRow ? `Edit ${lowerFirst(config.singular)}` : `Add ${lowerFirst(config.singular)}`
        }
        footer={
          <>
            <Button variant="secondary" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button type="submit" form={formId}>
              {editRow ? 'Save changes' : 'Create'}
            </Button>
          </>
        }
      >
        {editing && (
          <ResourceForm
            key={editRow ? keyOf(editRow) : 'new'}
            formId={formId}
            fields={config.fields}
            schema={config.schema}
            defaultValues={editRow ? config.toForm(editRow) : config.defaults(lookups)}
            isEdit={!!editRow}
            lookups={lookups}
            onSubmit={save}
          />
        )}
      </Modal>

      <ConfirmDialog
        open={deleting !== null}
        title={`Delete ${lowerFirst(config.singular)}?`}
        message={
          <>
            This permanently removes the record. Records that are already used by other data cannot
            be deleted — mark them <strong>Inactive</strong> instead so history stays intact.
          </>
        }
        confirmLabel="Delete"
        loading={remove.isPending}
        onConfirm={() => deleting && remove.mutate(deleting)}
        onCancel={() => setDeleting(null)}
      />
    </>
  );
}
